import { useMemo } from 'react'

import { AxiosResponse } from 'axios'

import {
  CENTER_OF_EXCELLENCE,
  COMMON_MIXPANEL_PAGE,
  DEFAULT_SPECULATIVE_FILTER_THRESHOLDS,
  Episode,
  isLanguageEnglish,
  PROVIDER_SUPPLY_GROUP,
  RiskFactorsResponse,
  SearchRequestData,
  SpeculativeSearchThresholds,
  TOOLKIT_TYPE,
  TriageSearch,
  useRunSpeculativeSearch,
  WELLNESS_TOOLKIT_PROMOTION,
} from '@lyrahealth-inc/shared-app-logic'

import { SELECT_ELIGIBLE_TOOLKITS, WFMH_ELIGIBLE_TOOLKITS } from '../constants'
import { WellnessToolkitAvailableConfig } from '../types'
import {
  evaluateShortCircuitEligibility,
  evaluateToolkitAvailability,
  getWellnessToolkitAvailableConfig,
  isLatestCompletedSearchNotTierThree,
  isNotInActiveEpisode as isNotInActiveEpisodeCheck,
  isNotSelfHarmOrSuicialIdeationIndicated,
  ToolkitAvailabilityReason,
} from '../wellnessToolkitUtil'

export type ToolkitAvailabilityState = {
  isToolkitAvailable: boolean
  reasons: ToolkitAvailabilityReason[]
}

type ToolkitBaseConfig = {
  conditions: WellnessToolkitAvailableConfig
  configExtras?: WellnessToolkitAvailableConfig['configExtras']
  isFeatureFlagEnabled?: boolean
}

export interface useToolkitAvailabilityMapParams {
  isLyraSelect: boolean
  page?: COMMON_MIXPANEL_PAGE
  lyraId: string
  isEssentialsEnabled: boolean
  isCoachingEnabled: boolean
  isTherapyEnabled: boolean
  isCoachingForParentsEnabled: boolean
  hideChildSearchCTA?: boolean
  hideDependentField?: boolean
  isUserTeen: boolean
  triageSearches: TriageSearch[]
  episodes: Episode[]
  isUserLoggedIn: boolean
  isInternationalUser: boolean
  postSpeculativeSearch: (lyraId: string, searchData: SearchRequestData) => Promise<AxiosResponse<boolean>>
  riskFactors?: RiskFactorsResponse
  userDOB?: string
  userCountryName?: string
  displayLanguage?: string
  isFamilyHubEnabled?: boolean
  isCoachingEntryPointEnabled?: boolean
  isCoachingForParentsEntryPointEnabled?: boolean
  isCriticalIncidentSupportToolkitEnabled?: boolean
  isNeurodiversityCOEEnabled?: boolean
  isSpeculativeFilterEnabled: boolean
  speculativeSearchThresholds?: SpeculativeSearchThresholds
}

/**
 * Evaluates availability for all toolkits (WFMH or Lyra Select) and returns a map of availability states.
 *
 * This hook performs comprehensive eligibility checks for each toolkit including:
 * - Short-circuit checks (feature flags, user attributes, essentials/coaching enabled)
 * - Clinical safety checks (SH/SI, T3 recommendations, active episodes)
 * - Provider supply checks (speculative search for coaching toolkits)
 *
 * The hook optimizes performance by:
 * - Running short-circuit checks first to skip expensive operations when possible
 * - Batching speculative searches for all eligible toolkits in a single request
 * - Memoizing results based on input parameters
 *
 * @returns {Object} Object containing:
 *   - availabilityByToolkit: Record mapping each toolkit to its availability state and reasons
 *   - isLoading: Boolean indicating if speculative search is in progress
 */
export const useToolkitAvailabilityMap = ({
  isLyraSelect,
  page,
  lyraId,
  isEssentialsEnabled,
  isCoachingEnabled,
  isTherapyEnabled,
  isCoachingForParentsEnabled,
  hideChildSearchCTA,
  hideDependentField,
  isUserTeen,
  triageSearches,
  episodes,
  isUserLoggedIn,
  isInternationalUser,
  postSpeculativeSearch,
  riskFactors,
  userDOB,
  userCountryName,
  displayLanguage,
  isFamilyHubEnabled,
  isCoachingEntryPointEnabled,
  isCoachingForParentsEntryPointEnabled,
  isCriticalIncidentSupportToolkitEnabled,
  isNeurodiversityCOEEnabled,
  isSpeculativeFilterEnabled,
  speculativeSearchThresholds = DEFAULT_SPECULATIVE_FILTER_THRESHOLDS,
}: useToolkitAvailabilityMapParams): {
  availabilityByToolkit: Record<TOOLKIT_TYPE, ToolkitAvailabilityState>
  isLoading: boolean
} => {
  const isUserDisplayLanguageEnglish = displayLanguage ? isLanguageEnglish(displayLanguage) : false

  const { eligibleToolkits, baseConfigByToolkit, passesShortCircuitByToolkit } = useMemo(() => {
    const featureFlagByToolkit: Partial<Record<TOOLKIT_TYPE, boolean>> = {
      [WELLNESS_TOOLKIT_PROMOTION.FAMILY]: isFamilyHubEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.WORK]: isCoachingEntryPointEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.PURPOSE]: isCoachingEntryPointEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS]: isCoachingEntryPointEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.PARENT]: isCoachingForParentsEntryPointEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: isCriticalIncidentSupportToolkitEnabled,
      [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: isNeurodiversityCOEEnabled,
    }

    const toolkits = isLyraSelect ? SELECT_ELIGIBLE_TOOLKITS : WFMH_ELIGIBLE_TOOLKITS

    const baseConfig: Partial<Record<TOOLKIT_TYPE, ToolkitBaseConfig>> = {}
    const shortCircuitMap: Partial<Record<TOOLKIT_TYPE, boolean>> = {}

    toolkits.forEach((toolkit) => {
      const conditions = getWellnessToolkitAvailableConfig({ toolkit, isLyraSelect, page })
      const { configExtras } = conditions
      const isFeatureFlagEnabled = featureFlagByToolkit[toolkit]

      baseConfig[toolkit] = {
        conditions,
        configExtras,
        isFeatureFlagEnabled,
      }

      const { hasPassedShortCircuitEligibilityChecks } = evaluateShortCircuitEligibility({
        toolkitLabel: toolkit,
        conditions,
        configExtras,
        isFeatureFlagEnabled,
        isEssentialsEnabled,
        isCoachingEnabled,
        isTherapyEnabled,
        isCoachingForParentsEnabled,
        hideChildSearchCTA,
        hideDependentField,
        isUserTeen,
        isInternationalUser,
        isUserDisplayLanguageEnglish,
      })

      shortCircuitMap[toolkit] = hasPassedShortCircuitEligibilityChecks
    })

    return {
      eligibleToolkits: toolkits,
      baseConfigByToolkit: baseConfig as Record<TOOLKIT_TYPE, ToolkitBaseConfig>,
      passesShortCircuitByToolkit: shortCircuitMap as Record<TOOLKIT_TYPE, boolean>,
    }
  }, [
    isFamilyHubEnabled,
    isCoachingEntryPointEnabled,
    isCoachingForParentsEntryPointEnabled,
    isCriticalIncidentSupportToolkitEnabled,
    isNeurodiversityCOEEnabled,
    isLyraSelect,
    page,
    isEssentialsEnabled,
    isCoachingEnabled,
    isTherapyEnabled,
    isCoachingForParentsEnabled,
    hideChildSearchCTA,
    hideDependentField,
    isUserTeen,
    isInternationalUser,
    isUserDisplayLanguageEnglish,
  ])

  const isNotSHOrSI = isNotSelfHarmOrSuicialIdeationIndicated(riskFactors)
  const isNotT3Recommendation = isLatestCompletedSearchNotTierThree(triageSearches)

  const parentCoachingSearchRequestData = useMemo(() => {
    const shouldHaveProviderSupply = baseConfigByToolkit[WELLNESS_TOOLKIT_PROMOTION.PARENT]
    const programSearchRequestData =
      baseConfigByToolkit[WELLNESS_TOOLKIT_PROMOTION.PARENT]?.configExtras?.programSearchRequestData

    if (
      !shouldHaveProviderSupply ||
      !passesShortCircuitByToolkit[WELLNESS_TOOLKIT_PROMOTION.PARENT] ||
      programSearchRequestData === undefined
    ) {
      return undefined
    }

    return programSearchRequestData
  }, [baseConfigByToolkit, passesShortCircuitByToolkit])

  const parentToolkitFilter = speculativeSearchThresholds
    ? speculativeSearchThresholds[PROVIDER_SUPPLY_GROUP.PARENT_COACHING]
    : undefined

  const parentSpeculativeSearchResult = useRunSpeculativeSearch({
    searchRequestData: parentCoachingSearchRequestData,
    isUserLoggedIn,
    lyraId,
    userDOB,
    userCountryName,
    isInternationalUser,
    postSpeculativeSearch,
    isSpeculativeFilterEnabled,
    ...(isSpeculativeFilterEnabled && parentToolkitFilter ? { filters: parentToolkitFilter } : {}),
  })

  const hasAvailableProviderSupply: Partial<Record<TOOLKIT_TYPE, boolean>> = useMemo(
    () => ({
      [WELLNESS_TOOLKIT_PROMOTION.PARENT]: parentSpeculativeSearchResult.hasAvailableSupply,
    }),
    [parentSpeculativeSearchResult],
  )

  const availabilityByToolkit = useMemo(() => {
    const result: Partial<Record<TOOLKIT_TYPE, ToolkitAvailabilityState>> = {}

    eligibleToolkits.forEach((toolkit) => {
      const baseConfig = baseConfigByToolkit[toolkit]
      if (!baseConfig) {
        return
      }

      const { conditions, configExtras, isFeatureFlagEnabled } = baseConfig

      const isNotInActiveEpisode = isNotInActiveEpisodeCheck(
        episodes,
        lyraId,
        configExtras?.allowedProgramsForActiveEpisodes,
      )

      const shouldHaveProviderSupply = conditions.shouldHaveProviderSupply && isUserLoggedIn

      const { isToolkitAvailable, reasons } = evaluateToolkitAvailability({
        toolkitLabel: toolkit,
        conditions,
        configExtras,
        isFeatureFlagEnabled,
        isEssentialsEnabled,
        isCoachingEnabled,
        isTherapyEnabled,
        isCoachingForParentsEnabled,
        hideChildSearchCTA,
        hideDependentField,
        isUserTeen,
        isInternationalUser,
        isUserDisplayLanguageEnglish,
        isNotSHOrSI,
        isNotInActiveEpisode,
        isNotT3Recommendation,
        shouldHaveProviderSupply,
        hasAvailableProviderSupply: conditions.shouldHaveProviderSupply
          ? hasAvailableProviderSupply[toolkit] ?? false
          : false,
      })

      result[toolkit] = {
        isToolkitAvailable,
        reasons,
      }
    })

    return result as Record<TOOLKIT_TYPE, ToolkitAvailabilityState>
  }, [
    eligibleToolkits,
    baseConfigByToolkit,
    episodes,
    lyraId,
    isUserLoggedIn,
    isEssentialsEnabled,
    isCoachingEnabled,
    isTherapyEnabled,
    isCoachingForParentsEnabled,
    hideChildSearchCTA,
    hideDependentField,
    isUserTeen,
    isInternationalUser,
    isUserDisplayLanguageEnglish,
    isNotSHOrSI,
    isNotT3Recommendation,
    hasAvailableProviderSupply,
  ])

  return {
    availabilityByToolkit,
    isLoading: parentSpeculativeSearchResult.isLoading,
  }
}
